import {TimelineLite} from "gsap";
import {RoadKill} from "../RoadKill";
import {RoadKillSymbol} from "../reels/RoadKillSymbol";
import {BasicBitmapCountUp} from "../component/BasicBitmapCountUp";
import {RoadKillGameAssets} from "../RoadKillGameAssets";
import {RoadKillModel} from "../server/RoadKillModel";
import {BelowStakeWinRestriction} from "../server/BelowStakeWinRestriction";
import {RoadKillGameConfig} from "../RoadKillGameConfig";
import {IWinHighLightData} from "../server/RoadKillData";

export class RoadKillWinToggle extends PIXI.Container {
    private _countUp: BasicBitmapCountUp;
    private _winToggleTL: TimelineLite | null;
    private _waysCount: PIXI.Text;

    constructor() {
        super();
        this.name = "winToggle";
        this._countUp = this.createCountUp();
        this._waysCount = this.createWaysCount();
        this.addChild(this._countUp, this._waysCount);
    }

    private _playToggle: boolean = true;

    set playToggle(play: boolean) {
        this._playToggle = true;
    }

    public checkToRun(): boolean {
        let returnValue: boolean = false,
            gameData: RoadKillModel = RoadKill.model;
        if (gameData.gameData && !gameData.isRestore && !BelowStakeWinRestriction.isWinBelowStake() && !gameData.brokeTheBank) {
            if (!RoadKill.autoPlayPlugin.isAutoplayRound && RoadKill.model.winDataHighlight.length > 0) {
                returnValue = true;
            }
        }
        return returnValue;
    }

    public async stopWinToggle(): Promise<any> {
        this._playToggle = false;
        if (this._winToggleTL) {
            this._countUp.visible = false;
            this._waysCount.visible = false;
            this._winToggleTL?.pause().kill();
        }
        this._winToggleTL = null;
        await Promise.resolve();
    }

    public preWinToggle(): TimelineLite {
        RoadKill.winToggle.playToggle = true;
        const tl: TimelineLite = new TimelineLite(),
            allWins: boolean[][] = RoadKill.model.allWinPositions;
        tl.add([RoadKill.initialWinView.hideCountUp(), RoadKill.bigWinView.hideCountUp()], 0);
        allWins.forEach((reel, reelId) => {
            reel.forEach((sym, symId) => {
                const symbol = RoadKill.reelView.getVisibleSymbolAt(reelId, symId);
                if (sym) {
                    tl.add(symbol.setSpineAlpha(), 0);
                } else {
                    tl.add(() => symbol.markNonWin(false), 0)
                }
            })
        })
        return tl;
    }


    public playWinToggle(): void {
        if (!this._playToggle) {
            this.hideIndividualWinToggle();
            return;
        }
        const tl: TimelineLite = new TimelineLite();
        const delay: number = 3 - RoadKillGameConfig.HIDE_AND_ALPHA_DURATION,
            duration: number = 1.2,
            gameData: RoadKillModel = RoadKill.model,
            allWins: boolean[][] = RoadKill.model.allWinPositions,
            totalComb: number = this.getTotalCombination();
        tl.to(this, {duration: delay});
        tl.add(() => {
            this._countUp.forceSetText(gameData.winDataHighlight[0].multipliedAmount);
            this._waysCount.visible = true;
            this._countUp.visible = true;
            this._countUp.alpha = 1;
            this._waysCount.alpha = 1;
        }, delay);

        for (let betWinIndex: number = 0; betWinIndex < gameData.winDataHighlight.length; betWinIndex++) {
            const betWinData: IWinHighLightData = gameData.winDataHighlight[betWinIndex];
            for (let reelId: number = 0; reelId < gameData.evaluatedArea.length; reelId++) {
                for (let symId: number = 0; symId < gameData.evaluatedArea[reelId].length; symId++) {
                    const sym: RoadKillSymbol = RoadKill.reelView.getVisibleSymbolAt(reelId, symId),
                        isWin: boolean = betWinData.reelWinPositions[reelId][symId];
                    tl.add(() => {
                        if (isWin) {
                            this.playWinAnim(sym);
                        }
                        this.playNonWinAnim(sym, !isWin);
                    }, delay + (betWinIndex * duration));
                }
            }
            if (this._playToggle) {
                tl.add(this.showIndividualWinToggle(betWinData.multipliedAmount, betWinData.multipliedAmount, betWinData.numberOfCombinations, duration, ""), delay + (betWinIndex * duration));
            }
        }

        if (gameData.winDataHighlight.length > 1) {
            for (let reel = 0; reel < allWins.length; reel++) {
                for (let row = 0; row < allWins[reel].length; row++) {
                    const sym: RoadKillSymbol = RoadKill.reelView.getVisibleSymbolAt(reel, row);
                    if (sym) {
                        tl.add(() => {
                            if (allWins[reel][row]) {
                                this.playNonWinAnim(sym, false);
                            }
                        }, delay + (gameData.winDataHighlight.length * duration))
                    }
                }
            }
        }
        if (this._playToggle) {
            tl.add(this.showIndividualWinToggle(RoadKill.model.gameData.totalSpinWinnings, RoadKill.model.gameData.totalSpinWinnings, totalComb, duration, ""), delay + (gameData.winDataHighlight.length == 1 ? 0 : (gameData.winDataHighlight.length * duration)));
        }

        tl.to(this, {duration: duration / 2});
        tl.add(() => {
            if (this._playToggle) {
                this.resetAllSymbols(allWins);
            }
        })

        if (this._playToggle) {
            tl.add(() => {
                this.playWinToggle()
            })
        }
        if (!this._playToggle) {
            tl?.pause().kill();
        }
        this._winToggleTL = tl;
    }

    private getTotalCombination(): number {
        const betData = RoadKill.model.winDataHighlight;
        let totalCombination: number = 0;
        for (let i = 0; i < betData.length; i++) {
            totalCombination += betData[i].numberOfCombinations;
        }
        return totalCombination;
    }

    private showIndividualWinToggle(from: number, to: number, combination: number, duration: number, ease: string): TimelineLite {
        const isBelowWinStake: boolean = BelowStakeWinRestriction.isWinBelowStake(),
            tl: TimelineLite = new TimelineLite({
                onStart: () => {
                    if (!this._countUp.visible || this._countUp.alpha === 0) {
                        this._countUp.visible = true;
                        this._countUp.alpha = 1;
                    }
                    if (!isBelowWinStake) {
                        if (!this._waysCount.visible || this._waysCount.alpha === 0) {
                            this._waysCount.visible = true;
                            this._waysCount.alpha = 1;
                        }
                    }
                }
            });
        tl.add([this._countUp.getCountAnimation(from, to, 0, ease), this.showWinWays(combination, isBelowWinStake)]);
        tl.add(() => {
            this._waysCount.x = 360;
        })
        tl.to(this, {duration: 1});
        return tl;
    }

    private showWinWays(combination: number, isBelowWinStake: boolean): TimelineLite {
        if (!isBelowWinStake) {
            return new TimelineLite().add(() => {
                this._waysCount.text = combination.toString() + " " + (combination === 1 ? RoadKill.apiPlugin.translations.translate("Way Won") : RoadKill.apiPlugin.translations.translate("Ways Won"));
                this._waysCount.updateTransform();
            })
        } else {
            return new TimelineLite();
        }
    }

    private hideIndividualWinToggle(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.to([this._countUp, this._waysCount], {alpha: 0, duration: 0.25});
        tl.add(() => {
            this._countUp.visible = false;
            this._waysCount.visible = false;
            this._waysCount.text = "";
        })
        return tl;
    }

    private createWaysCount(): PIXI.Text {
        const pixiText: PIXI.Text = new PIXI.Text("", new PIXI.TextStyle(RoadKillGameConfig.WAYS_STYLE));
        pixiText.alpha = 0;
        pixiText.visible = false;
        pixiText.anchor.set(0.5, 0.5);
        pixiText.position.set(360, 410);
        return pixiText;
    }

    private createCountUp(): BasicBitmapCountUp {
        const countUp: BasicBitmapCountUp = new BasicBitmapCountUp({
            fontName: RoadKillGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize: 100,
            tint: 0xffffff
        }, 720);
        countUp.visible = false;
        countUp.alpha = 0;
        countUp.position.set(360, 313);
        return countUp;
    }

    private resetAllSymbols(allWinPositions: boolean[][]): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        for (let reelId: number = 0; reelId < allWinPositions.length; ++reelId) {
            for (let symId: number = 0; symId < allWinPositions[reelId].length; symId++) {
                tl.add(() => {
                    RoadKill.reelView.getVisibleSymbolAt(reelId, symId).markNonWin(false, 0);
                }, 0);
            }
        }

        tl.add(this.hideIndividualWinToggle(), 0);
        return tl;
    }

    private playWinAnim(sym: RoadKillSymbol): TimelineLite {
        return new TimelineLite();
    }

    private playNonWinAnim(sym: RoadKillSymbol, isWin: boolean): TimelineLite {
        return new TimelineLite().add(() => {
            if (isWin) {
                sym.markNonWin(isWin);
            } else {
                sym.markNonWin(false, 0);
            }
        });
    }

}